class StoreLocation extends HTMLElement {
  constructor() {
    super();

    this.autocompleteService = null;
    this.placesService = null;
    this.stores = [];
    this.allowedCountries = null;
    this.debounceTimer = null;
    this.map = null;
    this.markers = [];
    this.userMarker = null;
    this.infoWindow = null;
    this.visibleLimit = 5;
    this.loadStep = 5;
    // How many stores to render on the initial (no-search) load. The full set
    // can be thousands of stores; rendering them all at once (a DOM card + 4
    // map markers each) is what made first load slow, so cap it to one page.
    this.initialVisible = 20;
    // Lazily-started promise for fetching the remaining metaobject pages, so
    // the heavy background fetch happens on first interaction, not on load.
    this.remainingPagesPromise = null;
    this.filteredStores = [];
    this.currentLatLng = null;
    this.loadMoreBtn = null;
    this.mapId = null;
    this.ripples = [];
    this.markerMap = new Map();
    this.fallbackCenter = { lat: 21.0285, lng: 105.8542 };
    this.radiusKm = Number(this.dataset.radius) || 100;
    this.activeMarker = null;
    this.activeShadow = null;
    this.markerCluster = null;
    this.pinLayerMap = new Map();
    // Viewport-driven rendering: as the map zooms/pans, the list + pins reflect
    // the stores inside the current map bounds (sorted by proximity to center),
    // capped so dense/zoomed-out views stay fast.
    this.viewportCap = 60;
    this.searchLocation = null; // pending search point, re-framed after full load
    this.programmaticMove = false; // true while WE move the camera (vs the user)
    this.viewportDebounce = null;
    this.lastViewportKey = null; // signature of last-rendered in-view set
    this.activeStoreIndex = null; // store whose info window is open, if any
    this.viewportNoteEl = null;
  }

  connectedCallback() {
    this.cacheElements();
    this.parseCountries();
    this.parseStoreData();
    this.initGoogleService();
    this.bindEvents();
    this.initMap();
    this.loadAllStores();
    // Note: the remaining metaobject pages are NOT fetched here. They load
    // lazily on first interaction (see ensureAllStoresLoaded) so the initial
    // page load isn't blocked by ~8 full-page fetches + a full re-render.
  }

  // Fetch the remaining store pages at most once, on demand. Idempotent: repeat
  // calls return the same in-flight/settled promise.
  ensureAllStoresLoaded() {
    if (this.dataset.storeSource !== "metaobject") return Promise.resolve();
    if (!this.remainingPagesPromise) {
      this.remainingPagesPromise = this.loadRemainingStorePages();
    }
    return this.remainingPagesPromise;
  }

  // When stores come from metaobjects, the embedded JSON only holds the first
  // page (Liquid caps metaobject loops at 250). Metaobjects paginate with an
  // opaque cursor (not ?page=N), so we follow the paginate.next.url chain that
  // the section exposes as `.storeNextPage`, merging each page, then re-render.
  async loadRemainingStorePages() {
    if (this.dataset.storeSource !== "metaobject") return;

    let nextUrl = this.querySelector(".storeNextPage")?.getAttribute("href");
    if (!nextUrl) return;

    // Track store indexes already loaded so we never merge duplicates.
    const seenIndexes = new Set(this.stores.map((store) => store.index));
    // Safety cap: Shopify paginate tops out at 25,000 items (100 pages of 250).
    let pageCount = 0;
    while (nextUrl && pageCount < 100) {
      pageCount += 1;
      let pageResult;
      try {
        pageResult = await this.fetchStorePage(nextUrl);
      } catch (error) {
        console.error("Failed to load store page", nextUrl, error);
        break;
      }
      const newStores = pageResult.stores.filter(
        (store) => !seenIndexes.has(store.index)
      );
      newStores.forEach((store) => seenIndexes.add(store.index));
      this.stores = this.stores.concat(newStores);
      if (!newStores.length) break; // no new stores -> stop
      nextUrl = pageResult.nextUrl;
    }

    const center = this.getCenterFromStores(this.stores);
    if (center) this.fallbackCenter = center;

    // If the user searched while pages were still loading, re-apply that search
    // (now across the full set) instead of clobbering it with "show all".
    // Newly-loaded stores may fall inside the current view (or nearer a search),
    // so force the next render to rebuild rather than short-circuit.
    this.lastViewportKey = null;

    if (!this.map) {
      this.renderFallbackList();
      return;
    }

    if (this.searchLocation) {
      // Re-frame now that the true nearest stores are available.
      this.frameLocation(this.searchLocation.lat, this.searchLocation.lng);
    }
    // Render in-view stores against the full set (frameLocation's idle will
    // also fire, but rendering now avoids a visible gap).
    this.renderViewport();
  }

  async fetchStorePage(pageUrl) {
    const url = new URL(pageUrl, location.origin);
    // Carry preview params so previews fetch the same (unpublished) theme.
    const currentParams = new URLSearchParams(location.search);
    for (const key of ["preview_theme_id", "_fd", "pb"]) {
      if (currentParams.has(key))
        url.searchParams.set(key, currentParams.get(key));
    }
    const response = await fetch(url.toString(), {
      headers: { "X-Requested-With": "fetch" },
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const doc = new DOMParser().parseFromString(
      await response.text(),
      "text/html"
    );

    let stores = [];
    const jsonEl = doc.querySelector(".dataStoreLocation");
    if (jsonEl) {
      try {
        const parsed = JSON.parse(jsonEl.textContent);
        if (Array.isArray(parsed)) stores = parsed;
      } catch (error) {
        console.error("Invalid store JSON on fetched page", error);
      }
    }
    const nextUrl =
      doc.querySelector(".storeNextPage")?.getAttribute("href") || null;
    return { stores, nextUrl };
  }

  cacheElements() {
    this.input = this.querySelector("#location-input");
    this.suggestionBox = this.querySelector(".location-suggestions");
    this.storeList = this.querySelector(".location-store-list");
    this.template = this.querySelector(".location-template");
    this.detectBtn = this.querySelector(".location-detect-btn");
    this.mapEl = this.querySelector(".location-map");
    this.template = this.querySelector(".location-template");
    this.storeCardTemplate =
      this.template?.content.querySelector(".store-card");
    this.loadMoreTemplate =
      this.template?.content.querySelector(".store-load-more");
    this.loadMoreBtn = null;
  }

  parseCountries() {
    const raw = this.dataset.countries;

    if (!raw) {
      this.allowedCountries = null;
      return;
    }

    const parsed = raw
      .split(",")
      .map((country) => country.trim().toLowerCase())
      .filter(Boolean);

    this.allowedCountries = parsed.length ? parsed : null;
  }

  parseStoreData() {
    const jsonEl = this.querySelector(".dataStoreLocation");
    if (!jsonEl) return;

    try {
      this.stores = JSON.parse(jsonEl.textContent);
      const center = this.getCenterFromStores(this.stores);
      if (center) {
        this.fallbackCenter = center;
      }
    } catch (e) {
      console.error("Invalid store JSON", e);
      this.stores = [];
    }
  }

  initGoogleService() {
    if (!window.google) return;

    this.autocompleteService = new google.maps.places.AutocompleteService();

    this.placesService = new google.maps.places.PlacesService(
      document.createElement("div")
    );
  }

  getCenterFromStores(stores) {
    if (!stores || !stores.length) return null;

    let latSum = 0;
    let lngSum = 0;

    stores.forEach((store) => {
      latSum += Number(store.lat);
      lngSum += Number(store.lng);
    });

    return {
      lat: latSum / stores.length,
      lng: lngSum / stores.length,
    };
  }

  bindEvents() {
    // Warm up the full store set as soon as the user engages with search, so
    // the data is ready by the time they pick a suggestion / hit enter.
    this.input.addEventListener(
      "focus",
      () => this.ensureAllStoresLoaded(),
      { once: true }
    );

    this.input.addEventListener("input", () => {
      clearTimeout(this.debounceTimer);

      this.debounceTimer = setTimeout(() => {
        this.handleSearch();
      }, 300);
    });

    document.addEventListener("click", (e) => {
      if (!this.contains(e.target)) {
        this.hideSuggestions();
      }
    });

    if (this.detectBtn) {
      this.detectBtn.addEventListener("click", () => {
        this.detectUserLocation();
      });
    }
    this.storeList.addEventListener("click", (e) => {
      if (
        e.target.closest(".store-direction-btn") ||
        e.target.closest(".store-hours") ||
        e.target.closest("button") ||
        e.target.closest("a") ||
        e.target.closest("summary")
      ) {
        return;
      }
      const card = e.target.closest(".store-card");
      if (!card) return;

      const storeIndex = Number(card.dataset.storeIndex);
      if (!storeIndex) return;

      this.focusStoreByIndex(storeIndex);
    });
  }

  initMap() {
    if (!this.mapEl || !window.google) return;

    this.map = new google.maps.Map(this.mapEl, {
      center: this.fallbackCenter,
      zoom: 12,
      zoomControl: true,
      scrollwheel: false,
    });
    this.infoWindow = new google.maps.InfoWindow();

    this.infoWindow.addListener("closeclick", () => {
      if (this.activeMarker && this.activeShadow) {
        this.setPinOpacity(this.activeMarker, 0.7);
        this.setShadowOpacity(this.activeShadow, 0.7);

        this.activeMarker = null;
        this.activeShadow = null;
      }
      this.activeStoreIndex = null;
    });

    this.map.addListener("click", () => {
      if (this.activeMarker && this.activeShadow) {
        this.setPinOpacity(this.activeMarker, 0.7);
        this.setShadowOpacity(this.activeShadow, 0.7);

        this.activeMarker = null;
        this.activeShadow = null;
      }

      this.activeStoreIndex = null;
      this.infoWindow.close();
    });

    // Re-render the in-view stores whenever the camera settles (zoom or pan).
    this.map.addListener("idle", () => {
      clearTimeout(this.viewportDebounce);
      this.viewportDebounce = setTimeout(() => {
        this.renderViewport();
        // The camera has settled; subsequent moves are the user's.
        this.programmaticMove = false;
      }, 150);
    });

    // The first time the user themselves moves the map, make sure the full
    // store set is loaded so panning/zooming can reveal every store.
    const onUserGesture = () => {
      if (this.programmaticMove) return;
      this.ensureAllStoresLoaded();
    };
    this.map.addListener("dragstart", onUserGesture);
    this.map.addListener("zoom_changed", onUserGesture);
  }

  // Render the stores that fall within the current map viewport: update the
  // list and the pins to match, sorted by distance from the map center and
  // capped for performance. This is the single source of truth for what shows.
  renderViewport() {
    if (!this.map) {
      this.renderFallbackList();
      return;
    }
    const bounds = this.map.getBounds();
    if (!bounds) return;

    const center = this.map.getCenter();
    const centerLat = center.lat();
    const centerLng = center.lng();

    const inView = [];
    for (const store of this.stores) {
      const lat = Number(store.lat);
      const lng = Number(store.lng);
      if (!lat && !lng) continue;
      if (!bounds.contains(new google.maps.LatLng(lat, lng))) continue;
      inView.push({
        ...store,
        distance: this.calcDistance(centerLat, centerLng, lat, lng),
      });
    }
    inView.sort((a, b) => a.distance - b.distance);

    const totalInView = inView.length;
    const capped = inView.slice(0, this.viewportCap);

    // Skip the rebuild when the visible set hasn't actually changed (e.g. a
    // small pan within the same cluster) so an open info window doesn't flicker.
    const key = capped.map((s) => s.index).join(",");
    if (key === this.lastViewportKey) {
      this.renderViewportNote(totalInView);
      return;
    }
    this.lastViewportKey = key;

    this.filteredStores = capped;
    this.visibleLimit = capped.length;

    this.renderStores(capped);
    this.renderStoreMarkers(capped, false); // false = don't move the camera
    this.syncDetailLayers();
    this.reopenActivePopup();
    this.renderViewportNote(totalInView);
  }

  // After markers are rebuilt, restore the open info window if its store is
  // still in view; otherwise close it.
  reopenActivePopup() {
    if (this.activeStoreIndex == null) return;
    const marker = this.markerMap.get(this.activeStoreIndex);
    const store = this.filteredStores.find(
      (s) => s.index === this.activeStoreIndex
    );
    if (marker && store) {
      this.setPinOpacity(marker, 1);
      const layers = this.pinLayerMap.get(marker);
      if (layers) this.setShadowOpacity(layers.shadow, 1);
      this.activeMarker = marker;
      this.activeShadow = layers ? layers.shadow : null;
      this.infoWindow.setContent(this.buildInfoWindowContent(store));
      this.infoWindow.open(this.map, marker);
    } else {
      this.activeStoreIndex = null;
      this.infoWindow.close();
    }
  }

  // Small status line above the list for the empty / truncated-by-cap cases.
  renderViewportNote(totalInView) {
    if (!this.storeList) return;
    if (!this.viewportNoteEl) {
      this.viewportNoteEl = document.createElement("div");
      this.viewportNoteEl.className =
        "location-viewport-note fs-small body_weight mb-sp-2";
      this.storeList.before(this.viewportNoteEl);
    }
    if (this.filteredStores.length === 0) {
      this.viewportNoteEl.textContent =
        "No stores in this area — zoom out or search a pincode / city.";
      this.viewportNoteEl.style.display = "";
    } else if (totalInView > this.viewportCap) {
      this.viewportNoteEl.textContent = `Showing the ${this.viewportCap} closest of ${totalInView} stores in view — zoom in to narrow down.`;
      this.viewportNoteEl.style.display = "";
    } else {
      this.viewportNoteEl.style.display = "none";
    }
  }

  // Frame the map around the given stores; the idle handler then renders
  // whatever ends up inside the resulting viewport.
  fitToStores(stores) {
    if (!this.map || !stores || !stores.length) return;
    const bounds = new google.maps.LatLngBounds();
    stores.forEach((s) => {
      const lat = Number(s.lat);
      const lng = Number(s.lng);
      if (lat || lng) bounds.extend(new google.maps.LatLng(lat, lng));
    });
    if (bounds.isEmpty()) return;
    this.programmaticMove = true;
    this.map.fitBounds(bounds);
  }

  // Center the map on a searched point, framed to include the nearest stores so
  // results are visible even where stores are sparse.
  frameLocation(lat, lng) {
    if (!this.map) {
      this.renderFallbackList();
      return;
    }
    const nearest = this.stores
      .map((s) => ({
        s,
        d: this.calcDistance(lat, lng, Number(s.lat), Number(s.lng)),
      }))
      .sort((a, b) => a.d - b.d)
      .slice(0, 5);

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(lat, lng));
    nearest.forEach(({ s }) =>
      bounds.extend(new google.maps.LatLng(Number(s.lat), Number(s.lng)))
    );
    this.programmaticMove = true;
    this.map.fitBounds(bounds);
  }

  // Entry point for a search / detected location: remember it, ensure the full
  // set is loading, and frame the map (idle → renderViewport does the rest).
  focusSearchLocation(lat, lng) {
    this.searchLocation = { lat, lng };
    this.currentLatLng = { lat, lng };
    this.ensureAllStoresLoaded();
    this.lastViewportKey = null; // force a re-render at the new location
    this.frameLocation(lat, lng);
  }

  loadAllStores() {
    if (!this.stores || this.stores.length === 0) return;
    if (!this.map) {
      // No Google Maps (e.g. bad/missing key): fall back to a simple paginated
      // list so the section still works.
      this.renderFallbackList();
      return;
    }
    // Frame all currently-loaded stores; the idle handler then renders only the
    // stores inside the resulting viewport (capped), so we never build markers
    // for thousands of stores at once — that was the initial-load bottleneck.
    this.fitToStores(this.stores);
    this.renderViewport();
  }

  // List-only rendering used when Google Maps is unavailable. Shows one page
  // with the existing "Load more" button.
  renderFallbackList() {
    if (!this.storeList || !this.stores || !this.stores.length) return;
    this.filteredStores = [...this.stores];
    this.visibleLimit = Math.min(this.initialVisible, this.stores.length);
    this.renderStores(this.filteredStores.slice(0, this.visibleLimit));
  }

  isStoreClosedToday(store) {
    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    const today = days[new Date().getDay()];
    return store[today]?.schedule === "closed";
  }

  buildInfoWindowContent(store) {
    const template = this.querySelector(".location-template-infowindow");
    if (!template) return null;
    const node = template.content.firstElementChild.cloneNode(true);
    const isClosedToday = this.isStoreClosedToday(store);
    const nameEl = node.querySelector(".store-name");
    if (nameEl) nameEl.textContent = store.name || "";
    const addressWrap = node
      .querySelector(".store-address")
      .closest(".store-info");
    if (store.address) {
      addressWrap.querySelector(".store-address").textContent = store.address;
    } else {
      addressWrap.remove();
    }
    const phoneWrap = node.querySelector(".store-phone").closest(".store-info");
    if (store.phone) {
      phoneWrap.querySelector(".store-phone").textContent = store.phone;
    } else {
      phoneWrap.remove();
    }
    const emailWrap = node.querySelector(".store-email").closest(".store-info");
    if (store.email) {
      emailWrap.querySelector(".store-email").textContent = store.email;
    } else {
      emailWrap.remove();
    }

    const days = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];
    const todayKey = days[new Date().getDay()];
    const todayData = store[todayKey];

    const statusEl = node.querySelector(".opening-hours");
    const store_status = node.querySelector(".store-status");

    if (!todayData || todayData.schedule === "closed") {
      statusEl.textContent = "Store Closed";
      store_status.classList.add("closed");
      store_status.classList.remove("open");
    } else {
      const timeRange = this.formatTimeRange(todayData.open, todayData.close);
      statusEl.textContent = `${timeRange}`;
      store_status.classList.add("open");
      store_status.classList.remove("closed");
    }

    const direction = node.querySelector(".store-direction-btn");
    direction.href = `https://www.google.com/maps?q=${store.lat},${store.lng}`;
    return node;
  }

  detectUserLocation() {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser.");
      return;
    }

    this.detectBtn.classList.add("loading");
    this.suggestionBox.classList.add("hidden");
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        this.reverseGeocode(lat, lng);
        this.focusSearchLocation(lat, lng);
        this.detectBtn.classList.remove("loading");
      },
      (error) => {
        this.detectBtn.classList.remove("loading");

        switch (error.code) {
          case error.PERMISSION_DENIED:
            alert("Please allow location access.");
            break;
          case error.POSITION_UNAVAILABLE:
            alert("Location information is unavailable.");
            break;
          case error.TIMEOUT:
            alert("Location request timed out.");
            break;
          default:
            alert("Unable to retrieve your location.");
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  }

  renderUserMarker(lat, lng) {
    if (!this.map) return;

    if (this.userMarker) {
      this.userMarker.setMap(null);
    }

    this.map.setCenter({ lat, lng });
  }

  reverseGeocode(lat, lng) {
    if (!window.google) return;
    const geocoder = new google.maps.Geocoder();
    geocoder.geocode({ location: { lat, lng } }, (results, status) => {
      if (status === "OK" && results && results.length) {
        const result =
          results.find(
            (r) =>
              r.types.includes("locality") ||
              r.types.includes("administrative_area_level_1")
          ) || results[0];

        this.input.value = result.formatted_address;
      }
    });
  }

  handleSearch() {
    const value = this.input.value.trim();
    this.clearSuggestions();

    if (!value) {
      this.resetToInitialState();
      return;
    }

    if (!value || !this.autocompleteService) return;

    const results = [];
    let completed = 0;

    const handleResponse = (predictions, status) => {
      completed++;

      if (status === google.maps.places.PlacesServiceStatus.OK && predictions) {
        predictions.forEach((p) => {
          if (!results.find((r) => r.place_id === p.place_id)) {
            results.push(p);
          }
        });
      }

      if (completed === 2 && results.length) {
        this.renderSuggestions(results);
      }
    };

    const buildRequest = (type) => {
      const req = {
        input: value,
        types: [type],
      };

      if (this.allowedCountries) {
        req.componentRestrictions = {
          country: this.allowedCountries,
        };
      }

      return req;
    };

    this.autocompleteService.getPlacePredictions(
      buildRequest("(cities)"),
      handleResponse
    );

    // "postal_code" is NOT a valid type for the Autocomplete service (it only
    // accepts geocode/address/establishment/(regions)/(cities)) and returns
    // INVALID_REQUEST, so PIN/postal code searches came back empty. The
    // "(regions)" collection includes postal_code, so pincode search works.
    this.autocompleteService.getPlacePredictions(
      buildRequest("(regions)"),
      handleResponse
    );
  }

  resetToInitialState() {
    this.currentLatLng = null;
    this.searchLocation = null;
    this.activeStoreIndex = null;
    this.lastViewportKey = null;

    if (this.userMarker) {
      this.userMarker.setMap(null);
      this.userMarker = null;
    }
    if (this.infoWindow) {
      this.infoWindow.close();
    }

    if (this.stores && this.stores.length) {
      // Restore the broad view; the idle handler re-renders the in-view set.
      this.loadAllStores();
    } else if (this.storeList) {
      this.filteredStores = [];
      this.storeList.innerHTML = "";
    }
  }

  renderSuggestions(predictions) {
    this.suggestionBox.innerHTML = "";

    predictions.forEach((p) => {
      const li = document.createElement("li");
      li.className =
        "location-suggestion-item hover-bg_second rounded-10 transition px-sp-3 py-sp-2 pointer";
      li.textContent = p.description;

      li.addEventListener("click", () => {
        this.input.value = p.description;
        this.hideSuggestions();
        this.resolvePlace(p.place_id);
      });

      this.suggestionBox.appendChild(li);
    });

    this.suggestionBox.classList.remove("hidden");
  }

  clearSuggestions() {
    this.suggestionBox.innerHTML = "";
    this.hideSuggestions();
  }

  hideSuggestions() {
    this.suggestionBox.classList.add("hidden");
  }

  resolvePlace(placeId) {
    this.placesService.getDetails({ placeId }, (place, status) => {
      if (
        status === google.maps.places.PlacesServiceStatus.OK &&
        place.geometry
      ) {
        const lat = place.geometry.location.lat();
        const lng = place.geometry.location.lng();
        this.focusSearchLocation(lat, lng);
      }
    });
  }

  filterStoresByDistance(lat, lng) {
    // Remember the active search so background-loaded pages can re-filter.
    this.currentLatLng = { lat, lng };
    // Make sure we filter across every store. If pages are still loading, the
    // completion handler re-filters using this.currentLatLng (set above).
    this.ensureAllStoresLoaded();
    this.filteredStores = this.stores
      .map((store) => {
        const distance = this.calcDistance(lat, lng, store.lat, store.lng);

        return {
          ...store,
          distance,
        };
      })
      .filter((store) => store.distance <= this.radiusKm)
      .sort((a, b) => a.distance - b.distance);
    this.visibleLimit = 5;
    this.renderVisibleStores();
  }

  handleLoadMore() {
    // Paging past the first page needs the full set loaded.
    this.ensureAllStoresLoaded();
    this.visibleLimit += this.loadStep;
    this.renderVisibleStores();
  }

  renderVisibleStores() {
    const visibleStores = this.filteredStores.slice(0, this.visibleLimit);

    this.renderStores(visibleStores);
    this.renderStoreMarkers(visibleStores);
    this.syncDetailLayers();
    this.toggleLoadMoreButton();
  }

  toggleLoadMoreButton() {
    if (!this.loadMoreBtn) return;
    const hasMore = this.visibleLimit < this.filteredStores.length;
    this.loadMoreBtn.style.display = hasMore ? "block" : "none";
  }

  createPinMarker(store, opacity = 0.75) {
    return new google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      map: this.map,
      icon: {
        path: `
        M12 1
        C6.48 1 2 5.48 2 11
        C2 16.52 12 27 12 27
        C12 27 22 16.52 22 11
        C22 5.48 17.52 1 12 1
        Z
      `,
        fillColor: "#111111",
        fillOpacity: opacity,
        strokeColor: "#111111",
        strokeWeight: 0,
        scale: 1.5,
        anchor: new google.maps.Point(12, 27),
      },
      zIndex: 40,
    });
  }

  createInnerCircle(store) {
    return new google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      map: this.map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: "#ffffff",
        fillOpacity: 1,
        strokeOpacity: 0,
        anchor: new google.maps.Point(0, 2.5),
      },
      zIndex: 45,
      clickable: false,
    });
  }

  createNumberLabel(store, order) {
    return new google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      map: this.map,
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillOpacity: 0,
        strokeOpacity: 0,
        anchor: new google.maps.Point(0, 2.5),
      },
      label: {
        text: String(order),
        color: "#111111",
        fontSize: "14px",
        fontWeight: "500",
      },
      zIndex: 50,
      clickable: false,
    });
  }

  createExactPinShadow(store, opacity = 0.75) {
    return new google.maps.Marker({
      position: { lat: store.lat, lng: store.lng },
      map: this.map,
      icon: {
        path: "M -8 0 A 8 3 0 1 0 8 0 A 8 3 0 1 0 -8 0",
        fillColor: "#000000",
        fillOpacity: opacity,
        strokeOpacity: 0,
        scale: 1,
        anchor: new google.maps.Point(0, -5),
      },
      zIndex: 5,
      clickable: false,
    });
  }

  setPinOpacity(pin, opacity) {
    if (!pin) return;
    const icon = pin.getIcon();
    if (!icon) return;

    pin.setIcon({
      ...icon,
      fillOpacity: opacity,
    });
  }

  setShadowOpacity(shadow, opacity) {
    if (!shadow) return;
    const icon = shadow.getIcon();
    if (!icon) return;

    shadow.setIcon({
      ...icon,
      fillOpacity: opacity,
    });
  }

  renderStoreMarkers(stores, fit = true) {
    if (!this.map) return;
    if (this.markerCluster) {
      this.markerCluster.clearMarkers();
      this.markerCluster = null;
    }
    this.markers.forEach(({ pin, circle, label, shadow }) => {
      pin.setMap(null);
      circle.setMap(null);
      label.setMap(null);
      shadow.setMap(null);
    });

    this.markers = [];
    this.markerMap.clear();

    const bounds = new google.maps.LatLngBounds();
    const clusterPins = [];

    if (this.userMarker) {
      bounds.extend(this.userMarker.getPosition());
    }

    stores.forEach((store, order) => {
      const index = order + 1;
      const shadow = this.createExactPinShadow(store, 0.7);
      const pin = this.createPinMarker(store, 0.7);
      const circle = this.createInnerCircle(store);
      const label = this.createNumberLabel(store, index);

      pin.addListener("click", () => {
        this.markers.forEach(({ pin: p, shadow: s }) => {
          this.setPinOpacity(p, 0.7);
          this.setShadowOpacity(s, 0.7);
        });
        this.setPinOpacity(pin, 1);
        this.setShadowOpacity(shadow, 1);

        this.activeMarker = pin;
        this.activeShadow = shadow;
        this.activeStoreIndex = store.index;

        this.infoWindow.setContent(this.buildInfoWindowContent(store));
        this.infoWindow.open(this.map, pin);
      });

      this.markerMap.set(store.index, pin);
      this.markers.push({ pin, circle, label, shadow });
      this.pinLayerMap.set(pin, { circle, label, shadow });

      clusterPins.push(pin);
      bounds.extend(pin.getPosition());
    });

    this.markerCluster = new markerClusterer.MarkerClusterer({
      map: this.map,
      markers: clusterPins,
      renderer: {
        render: ({ count, position, markers }) => {
          if (markers.length > 1) {
            markers.forEach((pin) => {
              const layers = this.pinLayerMap.get(pin);
              if (layers) {
                layers.circle.setMap(null);
                layers.label.setMap(null);
                layers.shadow.setMap(null);
              }
            });
          } else {
            markers.forEach((pin) => {
              const layers = this.pinLayerMap.get(pin);
              if (layers) {
                layers.circle.setMap(this.map);
                layers.label.setMap(this.map);
                layers.shadow.setMap(this.map);
              }
            });
          }

          return new google.maps.Marker({
            position,
            label: {
              text: String(count),
              color: "#ffffff",
              fontSize: "14px",
              fontWeight: "700",
            },
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: 18,
              fillColor: "#111111",
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
            zIndex: 1000,
          });
        },
      },
    });

    // Only move the camera when explicitly asked (fit=true). Viewport renders
    // pass fit=false: moving the camera there would retrigger idle → render and
    // loop forever.
    if (fit) {
      this.programmaticMove = true;
      if (!bounds.isEmpty()) {
        this.map.fitBounds(bounds);
      }
      if (!this.filteredStores || this.filteredStores.length === 0) {
        this.map.setZoom(12);
      }
    }
  }

  syncDetailLayers() {
    if (!this.markerCluster) return;

    this.pinLayerMap.forEach((layers, pin) => {
      const isVisible = pin.getMap() === this.map;

      layers.circle.setMap(isVisible ? this.map : null);
      layers.label.setMap(isVisible ? this.map : null);
      layers.shadow.setMap(isVisible ? this.map : null);
    });
  }

  calcDistance(lat1, lng1, lat2, lng2) {
    // Haversine great-circle distance in kilometers.
    const toRad = (degrees) => (degrees * Math.PI) / 180;
    const earthRadiusKm = 6371;

    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const haversine =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;

    return (
      earthRadiusKm *
      2 *
      Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine))
    );
  }

  renderVisibleStores() {
    const visible = this.filteredStores.slice(0, this.visibleLimit);
    this.renderStores(visible);
    this.renderStoreMarkers(visible);
    this.syncDetailLayers();
  }

  renderStores(stores) {
    if (!this.storeList) return;

    this.storeList.innerHTML = "";

    stores.forEach((store, index) => {
      const card = this.renderStore(store, index + 1);
      if (card) {
        this.storeList.appendChild(card);
      }
    });

    const shouldShowLoadMore = this.filteredStores.length > this.visibleLimit;
    if (shouldShowLoadMore) {
      this.appendLoadMoreButton();
    } else {
      this.removeLoadMoreButton();
    }
  }

  appendLoadMoreButton() {
    if (this.loadMoreBtn || !this.loadMoreTemplate) return;

    const btn = this.loadMoreTemplate.cloneNode(true);

    btn.addEventListener("click", () => {
      this.handleLoadMore();
    });

    this.loadMoreBtn = btn;
    this.storeList.after(btn);
  }

  removeLoadMoreButton() {
    if (!this.loadMoreBtn) return;

    this.loadMoreBtn.remove();
    this.loadMoreBtn = null;
  }

  renderStore(store, order) {
    const template = this.querySelector(".location-template");
    if (!template) return null;
    const node = template.content.firstElementChild.cloneNode(true);
    node.dataset.storeIndex = store.index;
    const nameEl = node.querySelector(".store-name");
    if (nameEl) nameEl.textContent = `${order}. ${store.name}` || "";
    const addressWrap = node
      .querySelector(".store-address")
      .closest(".store-info");
    if (store.address) {
      addressWrap.querySelector(".store-address").textContent = store.address;
    } else {
      addressWrap.remove();
    }
    const phoneWrap = node.querySelector(".store-phone").closest(".store-info");
    if (store.phone) {
      phoneWrap.querySelector(".store-phone").textContent = store.phone;
    } else {
      phoneWrap.remove();
    }
    const emailWrap = node.querySelector(".store-email").closest(".store-info");
    if (store.email) {
      emailWrap.querySelector(".store-email").textContent = store.email;
    } else {
      emailWrap.remove();
    }
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];

    const todayKey = days[new Date().getDay()];
    const todayData = store[todayKey];
    const todayEl = node.querySelector(".store-hours-today");
    if (todayData.schedule === "closed") {
      todayEl.textContent = "Closed";
      todayEl.classList.add("closed");
    } else {
      todayEl.textContent = this.formatTimeRange(
        todayData.open,
        todayData.close
      );
      todayEl.classList.add("open");
    }
    days.forEach((day) => {
      const row = node.querySelector(`li[data-day="${day}"]`);
      row.classList.add("flex", "space-between", "mb-sp-1");
      if (!row) return;
      const timeEl = row.querySelector(".time");
      if (store[day].schedule === "closed") {
        row.classList.add("closed");
        timeEl.textContent = "Closed";
      } else {
        row.classList.remove("closed");
        timeEl.textContent = this.formatTimeRange(
          store[day].open,
          store[day].close
        );
      }
    });
    const direction = node.querySelector(".store-direction-btn");
    direction.href = `https://www.google.com/maps?q=${store.lat},${store.lng}`;
    return node;
  }

  formatHour(hour) {
    // Convert a 24-hour value to a 12-hour "h:00 am/pm" label.
    const hour24 = Number(hour);
    const suffix = hour24 >= 12 ? "pm" : "am";
    const hour12 = hour24 % 12 === 0 ? 12 : hour24 % 12;
    return `${hour12}:00 ${suffix}`;
  }

  formatTimeRange(open, close) {
    return `${this.formatHour(open)} – ${this.formatHour(close)}`;
  }

  focusStoreByIndex(storeIndex) {
    const marker = this.markerMap.get(storeIndex);
    const store = this.filteredStores.find((s) => s.index === storeIndex);

    if (!marker || !store) return;
    // Remember the open store so the viewport rebuild (triggered by the zoom
    // below) re-opens its info window instead of dropping it.
    this.activeStoreIndex = storeIndex;
    this.programmaticMove = true;
    this.map.panTo(marker.getPosition());
    this.map.setZoom(17);
    this.infoWindow.setContent(this.buildInfoWindowContent(store));
    this.infoWindow.open(this.map, marker);
    this.highlightStoreCard(storeIndex);
  }

  highlightStoreCard(storeIndex) {
    this.storeList.querySelectorAll(".store-card").forEach((card) => {
      card.classList.toggle(
        "active",
        Number(card.dataset.storeIndex) === storeIndex
      );
    });
  }
}
if (!customElements.get("store-location")) {
  customElements.define("store-location", StoreLocation);
}
